import { Tooltip } from "@progress/kendo-react-tooltip";
import { Info as InfoIcon } from "lucide-react";
import * as React from "react";
import { CSSProperties } from "react";
import { renderTooltipText } from "@/components/Kendo/ToolTip/renderTooltipText";

const IconHint: React.FC<{
  hint: string;
  style?: CSSProperties;
  iconWidth?: number;
  // Legacy twin of components/Forms/IconHint.tsx. This copy is what MetadataForm
  // (Integration ID, SSO popup) renders, so fixes applied only to the other file
  // never reach those fields — the two are duplicated by hand and have drifted
  // before. Apply any change here to both, and prefer the Forms copy for new
  // callers; this one goes away with the Kendo Form migration.
}> = ({ hint, style = {}, iconWidth = 14 }) => {
  // See the Forms copy for why the overlay offsets are gated instead of merely
  // overridable: `style` spreads last, so `position: "unset"` cancels
  // `position`/`top` but leaves `transform: translateY(-50%)` shifting the icon
  // 7px out of its row.
  const overlay = style.position === undefined;

  return (
    <Tooltip
      anchorElement="target"
      openDelay={300}
      parentTitle={true}
      style={{ maxWidth: 400 }}
      showCallout={false}
      content={renderTooltipText}
    >
      <span
        title={hint}
        role="button"
        style={{
          ...{
            ...(overlay
              ? {
                  position: "absolute" as const,
                  right: "0.625rem",
                  // Half the 46px control, NOT 50% — `.k-form-field-wrap` also
                  // holds the hint/error text, so a percentage drops the icon
                  // below the input's centre line once an error renders. Box
                  // pinned to the glyph so the copy button's offset produces a
                  // real 5px gap.
                  top: "1.4375rem",
                  transform: "translateY(-50%)",
                }
              : {}),
            // `inline-flex` so inline callers' `verticalAlign: "middle"` applies.
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            boxSizing: "border-box",
            width: iconWidth,
            height: iconWidth,
            padding: 0,
            lineHeight: 1,
            marginRight: "0.3125rem",
            // `--tlx-icon-link` is the dedicated icon intent. It resolves to the
            // same azure-blue-500 as the `--tlx-text-link` this used to use, so
            // this is a naming fix, not a colour change.
            color: "var(--tlx-icon-link)",
            zIndex: 1,
          },
          ...style,
        }}
      >
        <InfoIcon width={iconWidth} height={iconWidth} />
      </span>
    </Tooltip>
  );
};

export default IconHint;
